/* eslint-disable guard-for-in */
import { reactive } from 'vue';
import { localization } from './l10n';

export default class Api {
    constructor() {
        this.api_root = process.env.VUE_APP_API_ROOT || '';
        this.client_id = process.env.VUE_APP_TOPOMATIC_CLIENT_ID || undefined;
        this.store = reactive({
            users: {},
            companies: {},
            groups: {},
            accounts: [],
            activated: false,
        });
        this.load_accounts();
        this.initializing = false;
        this.postInitializators = [];
        window.addEventListener('focus', () => {
            this.initialize();
        });
    }

    async initialize(notify, forced) {
        if (this.initializing) {
            return;
        }
        this.initializing = true;
        try {
            try {
                await this.load_users();
            } catch { /* empty */ }
            try {
                if (notify) {
                    await this.handle_authorization_activation(false);
                }
            } catch { /* empty */ }
            if (this.store.accounts.length > 0) {
                const account = this.store.accounts[0];
                if ((account.expires > Date.now()) || (forced)) {
                    const user = await this.login(account);
                    account.user = user;
                    this.save_accounts();
                }
                try {
                    if (notify) {
                        await this.handle_authorization_activation(true);
                    } else {
                        this.store.activated = true;
                    }
                } catch { /* empty */ }
            }
        } finally {
            this.initializing = false;
        }
        const postInitializators = this.postInitializators;
        this.postInitializators = [];
        const promises = [];
        for (let i = 0; i < postInitializators.length; i += 1) {
            const item = postInitializators[i];
            promises.push(item.promise().then((e) => item.resolve(e)).catch((e) => item.reject ?? item.reject(e)));
        }
        await Promise.all(promises);
    }

    install(app) {
        app.config.globalProperties.$api = this;
    }

    format_url(method, url) {
        return this.api_root + url;
    }

    get access_token() {
        return this.account?.access_token;
    }

    get account() {
        const accounts = this.accounts;
        if (accounts.length > 0) {
            return accounts[0];
        }
        return undefined;
    }

    get accounts() {
        try {
            const accounts = localStorage.getItem('accounts');
            if (accounts != null) {
                return JSON.parse(accounts);
            }
            return [];
        } catch {
            return [];
        }
    }

    format_headers(/* method, url */) {
        const account = this.account;
        if (account?.access_token) {
            return {
                Authorization: `${account.token_type} ${account.access_token}`,
            };
        }
        return {};
    }

    unauthorized_code(status) {
        switch (status) {
        case 401:
        case 403:
        case 408:
            return true;
        default:
            return false;
        }
    }

    async handle_authorization_error() {
        await this.handle_authorization_activation(false);
        if (this.client_id) {
            this.store.accounts = [];
            this.save_accounts();
        }
    }

    handle_authorization_activation(value) {
        return new Promise((resolve) => {
            if (this.store.activated !== value) {
                this.store.activated = value;
            }
            resolve();
        });
    }

    handle_fetch_error(e, resolve, reject) {
        if (this.unauthorized_code(e.status)) {
            const accounts = this.accounts;
            if (accounts.length > 0) {
                this.initialize(false, true).then(() => {
                    resolve(e);
                }).catch(() => {
                    this.handle_authorization_error().finally(() => reject(e));
                });
            } else {
                reject(e);
            }
        } else {
            reject(e);
        }
    }

    fetch_api_key(method, url, body) {
        return new Promise((resolve, reject) => {
            if (this.initializing || !this.authorized) {
                this.postInitializators.push({
                    promise: () => this.fetch_api_key(method, url, body),
                    resolve,
                    reject,
                });
            } else {
                fetch(`${url}&api_key=${this.access_token}`, {
                    method,
                    body,
                }).then((data) => {
                    if (data.ok) {
                        resolve(data);
                    } else {
                        this.handle_fetch_error(data, () => {
                            if (method.toLowerCase() === 'get') {
                                this.fetch_api_key(method, url, body).then(resolve).catch(reject);
                            } else {
                                reject();
                            }
                        }, reject);
                    }
                }).catch(reject);
            }
        });
    }

    async fetch_api_key_json(method, url, body) {
        const data = await this.fetch_api_key(method, url, body);
        const json = await data.json();
        return json;
    }

    fetch(method, url, body, headers, skeepErrors) {
        return new Promise((resolve, reject) => {
            if (this.initializing || !this.authorized) {
                this.postInitializators.push({
                    promise: () => this.fetch(method, url, body, headers),
                    resolve,
                    reject,
                });
            } else {
                fetch(this.format_url(method, url), {
                    method,
                    headers: {
                        ...this.format_headers(method, url),
                        ...(headers != null ? headers : {}),
                    },
                    body,
                }).then((data) => {
                    if (data.ok) {
                        resolve(data);
                    } else if (skeepErrors) {
                        reject();
                    } else {
                        this.handle_fetch_error(data, () => {
                            if (method.toLowerCase() === 'get') {
                                this.fetch(method, url, body).then(resolve).catch(reject);
                            } else {
                                reject();
                            }
                        }, reject);
                    }
                }).catch(reject);
            }
        });
    }

    async fetch_json(method, url, body, headers, skeepErrors) {
        const data = await this.fetch(method, url, body, headers, skeepErrors);
        const json = await data.json();
        return json;
    }

    get(url, headers, skeepErrors) {
        return this.fetch('GET', url, undefined, headers, skeepErrors);
    }

    get_json(url, headers, skeepErrors) {
        return this.fetch_json('GET', url, undefined, headers, skeepErrors);
    }

    put(url, body) {
        return this.fetch('PUT', url, body);
    }

    put_json(url, body) {
        return this.fetch_json('PUT', url, body);
    }

    post(url, body) {
        return this.fetch('POST', url, body);
    }

    post_json(url, body) {
        return this.fetch_json('POST', url, body);
    }

    delete(url, body) {
        return this.fetch('DELETE', url, body);
    }

    delete_json(url, body) {
        return this.fetch_json('DELETE', url, body);
    }

    upload(method, url, body, progress) {
        return new Promise((resolve, reject) => {
            if (this.initializing || !this.authorized) {
                this.postInitializators.push({
                    promise: () => this.upload(method, url, body, progress),
                    resolve,
                    reject,
                });
            } else {
                const onprogress = (e) => {
                    if (progress != null) {
                        progress(Math.round((e.loaded * 100) / e.total));
                    }
                };
                const onloaded = () => {
                    if (progress != null) {
                        progress(100);
                    }
                };
                const xhr = new XMLHttpRequest();
                if (xhr == null) {
                    reject();
                }
                const headers = this.format_headers(method, url);
                xhr.open(method, this.format_url(method, url), true);
                for (const header in headers) {
                    xhr.setRequestHeader(header, headers[header]);
                }
                xhr.upload.addEventListener('progress', onprogress, false);
                xhr.upload.addEventListener('load', onloaded, false);
                xhr.onreadystatechange = () => {
                    if (xhr.readyState === 4) {
                        xhr.onreadystatechange = null;
                        xhr.upload.removeEventListener('progress', onprogress, false);
                        xhr.upload.removeEventListener('load', onloaded, false);
                        if (xhr.status === 200) {
                            let data;
                            try {
                                data = JSON.parse(xhr.responseText);
                            } catch {
                                data = xhr.response;
                            }
                            resolve(data);
                        } else {
                            reject();
                        }
                    } else if (xhr.readyState === 2 && xhr.status !== 200) {
                        reject(xhr);
                    }
                };
                xhr.send(body);
            }
        });
    }

    find_user(email) {
        if (this.client_id) {
            return new Promise((resolve, reject) => {
                this.fetch_api_key_json('GET', `https://id.topomatic.ru/api/quser?email=${email}`)
                    .then((user) => {
                        if (user.uid == null) {
                            user.uid = user.email;
                        }
                        this.store_user(user);
                        resolve(user);
                    })
                    .catch(reject);
            });
        }
        return new Promise((resolve, reject) => {
            this.get_json(`/oauth/quser?uid=${email}`).then((user) => {
                this.store_user(user);
                resolve(user);
            }).catch(reject);
        });
    }

    fetch_user(id) {
        if (this.client_id) {
            return new Promise((resolve, reject) => {
                this.fetch_api_key_json('GET', `https://id.topomatic.ru/api/user/${id}?`)
                    .then(resolve)
                    .catch(reject);
            });
        }
        return this.get_json(`/oauth/user?id=${id}`);
    }

    fetch_groups(id) {
        if (this.client_id) {
            return new Promise((resolve, reject) => {
                this.fetch_api_key_json('GET', `https://id.topomatic.ru/api/teams?company=${id}`)
                    .then((json) => {
                        const groups = {};
                        for (let i = 0; i < json.teams.length; i += 1) {
                            const group = json.teams[i];
                            if (group.name === 'staff') {
                                group.name = localization.tr('Все');
                                group.desc = localization.tr('Все сотрудники');
                            } else if (group.name === 'admins') {
                                group.name = localization.tr('Администраторы');
                                group.desc = localization.tr('Администраторы организации');
                            }
                            groups[group.id] = group;
                        }
                        resolve({
                            groups,
                        });
                    }).catch(reject);
            });
        }
        return new Promise((resolve, reject) => {
            this.get_json(`/oauth/groups?id=${id}`).then((responce) => {
                const groups = {};
                for (const group of responce.groups) {
                    if (group.name === 'staff') {
                        group.name = localization.tr('Все');
                        group.desc = localization.tr('Все сотрудники');
                    } else if (group.id === responce.admins) {
                        group.name = localization.tr('Администраторы');
                        group.desc = localization.tr('Администраторы организации');
                        group.admins = true;
                    }
                    groups[group.id] = group;
                }
                resolve({
                    groups,
                });
            }).catch(reject);
        });
    }

    fetch_company(id) {
        if (this.client_id) {
            return new Promise((resolve, reject) => {
                this.fetch_api_key_json('GET', `https://id.topomatic.ru/api/company/${id}?`)
                    .then(resolve)
                    .catch(reject);
            });
        }
        return this.get_json(`/oauth/company?id=${id}`);
    }

    get_all_users() {
        const users = [];
        for (const id in this.store.users) {
            const user = this.store.users[id];
            if ((user.email != null) || (user.uid != null)) {
                users.push(user);
            }
        }
        return users;
    }

    store_user(user) {
        user.tick = Date.now();
        this.store.users[user.id] = user;
        Api.database().then((db) => {
            const transaction = db.transaction('users', 'readwrite');
            const store = transaction.objectStore('users');
            store.put(user);
        });
    }

    get_user(id, callback = undefined) {
        if (id == null) {
            return {};
        }
        let user = this.store.users[id];
        const now = Date.now();
        if ((user == null) || (now - user.tick > 10 * 60 * 1000)) {
            if (user == null) {
                user = { id };
                this.store.users[id] = user;
            }
            user.tick = now;
            this.fetch_user(id)
                .then((nuser) => {
                    if (nuser.uid == null) {
                        nuser.uid = nuser.email;
                    }
                    this.store_user(nuser);
                    if (callback != null) {
                        callback(nuser);
                    }
                })
                .catch();
        } else if (callback != null) {
            callback(user);
        }
        if (user.id === '68cd405d6e8533907872c2d6') {
            user.name = localization.tr('Виртуальный');
            user.patronymic = localization.tr('Топоматик');
            user.surname = localization.tr('Ассистент');
        }
        return user;
    }

    get_groups(owner, callback = undefined) {
        if (owner == null) {
            return {
                groups: {},
            };
        }
        let groups = this.store.groups[owner];
        const now = Date.now();
        if ((groups == null) || (now - groups.tick > 10 * 60 * 1000)) {
            if (groups == null) {
                groups = { id: owner, groups: {} };
                this.store.groups[owner] = groups;
            }
            groups.tick = now;
            this.fetch_groups(owner)
                .then((response) => {
                    const ngroups = {
                        id: owner,
                        groups: response.groups,
                        tick: now,
                    };
                    this.store.groups[owner] = ngroups;
                    Api.database().then((db) => {
                        const transaction = db.transaction('groups', 'readwrite');
                        const store = transaction.objectStore('groups');
                        store.put(ngroups);
                    });
                    if (callback != null) {
                        callback(ngroups.groups);
                    }
                })
                .catch();
        } else if (callback != null) {
            callback(groups.groups);
        }
        return groups.groups;
    }

    get_group(owner, id, callback = undefined) {
        if (id == null) {
            return {};
        }
        const groups = this.get_groups(owner, (cgroups) => {
            if (callback != null) {
                callback(cgroups[id]);
            }
        });
        return groups[id];
    }

    get_company(id, callback = undefined) {
        if (id == null) {
            return {};
        }
        let company = this.store.companies[id];
        const now = Date.now();
        if ((company == null) || (now - company.tick > 10 * 60 * 1000)) {
            if (company == null) {
                company = { id };
                this.store.companies[id] = company;
            }
            company.tick = now;
            this.fetch_company(id)
                .then((ncompany) => {
                    ncompany.tick = now;
                    this.store.companies[id] = ncompany;
                    Api.database().then((db) => {
                        const transaction = db.transaction('companies', 'readwrite');
                        const store = transaction.objectStore('companies');
                        store.put(ncompany);
                    });
                    if (callback != null) {
                        callback(ncompany);
                    }
                })
                .catch();
        } else if (callback != null) {
            callback(company);
        }
        return company;
    }

    save_accounts() {
        const accounts = JSON.stringify(this.store.accounts);
        localStorage.setItem('accounts', accounts);
    }

    load_accounts() {
        try {
            const accounts = localStorage.getItem('accounts');
            if (accounts != null) {
                this.store.accounts = JSON.parse(accounts);
            } else {
                this.store.accounts = [];
            }
        } catch {
            this.store.accounts = [];
        }
    }

    load_table(db, values, name) {
        return new Promise((resolve) => {
            const transaction = db.transaction(name);
            const store = transaction.objectStore(name);
            const request = store.openCursor();
            const loaded = {};
            request.onsuccess = () => {
                const cursor = request.result;
                if (cursor) {
                    const value = cursor.value;
                    loaded[value.id] = value;
                    cursor.continue();
                } else {
                    for (const id in loaded) {
                        values[id] = loaded[id];
                    }
                    resolve();
                }
            };
        });
    }

    load_users() {
        return new Promise((resolve, reject) => {
            Api.database().then((db) => {
                Promise.all([
                    this.load_table(db, this.store.users, 'users'),
                    this.load_table(db, this.store.companies, 'companies'),
                    this.load_table(db, this.store.groups, 'groups'),
                ]).then().finally(resolve);
            }).catch(reject);
        });
    }

    login(account) {
        if (this.client_id) {
            return new Promise((resolve, reject) => {
                fetch(`https://id.topomatic.ru/api/company?api_key=${account.access_token}`)
                    .then((data) => {
                        if (data.ok) {
                            data.json()
                                .then((me) => {
                                    if (me.uid == null) {
                                        me.uid = me.email;
                                    }
                                    if ((me.role != null) && (me.role.length != null)) {
                                        for (let i = 0; i < me.role.length; i += 1) {
                                            if (me.role[i] === 'admin') {
                                                me.admin = true;
                                                break;
                                            }
                                        }
                                    }
                                    resolve(me);
                                })
                                .catch(reject);
                        } else {
                            reject();
                        }
                    })
                    .catch((e) => {
                        this.store.accounts = [];
                        this.save_accounts();
                        reject(e);
                    });
            });
        }
        return new Promise((resolve, reject) => {
            fetch(this.format_url('POST', '/oauth/login'), {
                method: 'POST',
                headers: {
                    Authorization: `${account.token_type} ${account.access_token}`,
                },
            })
                .then((data) => {
                    if (data.ok) {
                        data.json()
                            .then((ndata) => {
                                const access_token = ndata.access_token;
                                if (access_token != null) {
                                    delete ndata.access_token;
                                    account.access_token = access_token;
                                    account.expires = Date.now() + ndata.expires_in;
                                    account.token_type = ndata.token_type;
                                }
                                const me = ndata.user;
                                if ((me.role != null) && (me.role.length != null)) {
                                    for (let i = 0; i < me.role.length; i += 1) {
                                        if (me.role[i] === 'admin') {
                                            me.admin = true;
                                            break;
                                        }
                                    }
                                }
                                resolve(me);
                            })
                            .catch(reject);
                    } else {
                        reject(data);
                    }
                })
                .catch(reject);
        });
    }

    logout(account) {
        if (this.client_id) {
            return new Promise(() => {
                localStorage.setItem('accounts', '[]');
                window.document.location = `https://id.topomatic.ru/logout?back_url=${encodeURIComponent(this.tid(''))}`;
            });
        }
        return new Promise((resolve) => {
            const accounts = this.accounts;
            for (let i = accounts.length - 1; i >= 0; i -= 1) {
                if (accounts[i].access_token === account.access_token) {
                    accounts.splice(i, 1);
                    break;
                }
            }
            this.store.accounts = accounts;
            this.save_accounts();
            this.handle_authorization_activation(false).finally(() => {
                fetch(this.format_url('POST', '/oauth/logout'), {
                    method: 'POST',
                    headers: {
                        Authorization: `${account.token_type} ${account.access_token}`,
                    },
                })
                    .then((data) => {
                        if (data.ok) {
                            resolve(account);
                        } else {
                            resolve(account);
                        }
                    })
                    .catch(resolve(account));
            });
        });
    }

    pwd(login, password) {
        return new Promise((resolve, reject) => {
            this.authority('')
                .then((authority) => {
                    const pwd = authority.pwd;
                    const encoder = new TextEncoder();
                    const access_token = window.btoa(String.fromCodePoint(...encoder.encode(`${login}:${password}`)));
                    fetch(this.format_url('GET', pwd.login), {
                        method: 'GET',
                        headers: {
                            Authorization: `Basic ${access_token}`,
                        },
                    })
                        .then((data) => {
                            if (data.ok) {
                                data.json()
                                    .then((json) => {
                                        resolve(json);
                                    })
                                    .catch(reject);
                            } else {
                                reject();
                            }
                        })
                        .catch(reject);
                })
                .catch(reject);
        });
    }

    tid(state) {
        return `https://id.topomatic.ru/oauth/authorize?response_type=token&client_id=${this.client_id}&scope=account+company&state=${encodeURIComponent(state)}`;
    }

    switch_account(account) {
        return new Promise((resolve, reject) => {
            const access_token = account.access_token;
            this.handle_authorization_activation(false).finally(() => {
                this.login(account)
                    .then((user) => {
                        account.user = user;
                        this.store.accounts.splice(0, 0, account);
                        this.save_accounts();
                        this.handle_authorization_activation(true).finally(() => resolve(account));
                    })
                    .catch(reject)
                    .finally(() => {
                        for (let i = this.store.accounts.length - 1; i >= 0; i -= 1) {
                            if (this.store.accounts[i].access_token === access_token) {
                                this.store.accounts.splice(i, 1);
                                this.save_accounts();
                                break;
                            }
                        }
                    });
            });
        });
    }

    authority(state) {
        if (this.client_id) {
            return new Promise((resolve) => {
                resolve({
                    tid: {
                        login: this.tid(state),
                    },
                });
            });
        }
        return new Promise((resolve, reject) => {
            const self = this;
            const uri = self.format_url('GET', `/oauth/authority?state=${encodeURIComponent(state)}`);
            fetch(uri, {
                method: 'GET',
            }).then((data) => {
                if (data.ok) {
                    data.json()
                        .then((json) => {
                            resolve(json);
                        })
                        .catch(reject);
                } else {
                    reject(data);
                }
            }).catch(reject);
        });
    }

    add_account(access_token, expires_in, token_type) {
        return new Promise((resolve, reject) => {
            const account = {
                access_token,
                token_type,
                expires: Date.now() + expires_in,
            };
            this.login(account)
                .then((user) => {
                    account.user = user;
                    const logouts = [];
                    if (!this.client_id) {
                        for (let i = this.store.accounts.length - 1; i >= 0; i -= 1) {
                            const a = this.store.accounts[i];
                            if ((a.user.authority === account.user.authority) && (a.user.uid === account.user.uid)) {
                                if ((a.user.uid != null) || (a.user.email === account.user.email)) {
                                    logouts.push(this.logout(a));
                                }
                            }
                        }
                    } else {
                        for (let i = this.store.accounts.length - 1; i >= 0; i -= 1) {
                            logouts.push(this.logout(this.store.accounts[i]));
                        }
                    }
                    Promise.all(logouts).then().finally(() => {
                        this.store.accounts.splice(0, 0, account);
                        this.save_accounts();
                        this.handle_authorization_activation(false).finally(() => {
                            this.handle_authorization_activation(true).finally(() => {
                                resolve(account);
                            });
                        });
                    });
                })
                .catch(reject);
        });
    }

    get me() {
        if (this.authorized) {
            return this.store.accounts[0];
        }
        return undefined;
    }

    get authorized() {
        return this.store.activated && this.store.accounts.length > 0;
    }

    get multiaccount() {
        return !this.client_id;
    }

    static database() {
        return new Promise((resolve, reject) => {
            const req = indexedDB.open('cache_db', 1);
            req.onupgradeneeded = (e) => {
                const db = req.result;
                switch (e.oldVersion) {
                case 0:
                    db.createObjectStore('users', {
                        keyPath: 'id',
                    });
                    db.createObjectStore('companies', {
                        keyPath: 'id',
                    });
                    db.createObjectStore('groups', {
                        keyPath: 'id',
                    });
                    break;
                default:
                    break;
                }
            };
            req.onsuccess = () => {
                const db = req.result;
                resolve(db);
            };
            req.onerror = (e) => {
                reject(e);
            };
        });
    }
}
